import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, employeePayrollStats, parseYMD, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { StatCard, StatusBadge, TableShell, Modal, Button, PrimaryButton, Field, Input } from "@/components/ui-kit";
import PayrollReport from "@/components/PayrollReport";
import {
  ArrowLeft, Mail, Phone, Clock, Briefcase, DollarSign, Calendar,
  Users, Star, TrendingUp, Wallet, Printer, Download, FileText,
} from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";

export default function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [company, setCompany] = useState({});
  const [loaded, setLoaded] = useState(false);

  const [reportOpen, setReportOpen] = useState(false);
  const [mode, setMode] = useState("month"); // month | ytd | all | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/employees"), api.get("/payroll"), api.get("/jobs"), api.get("/settings")]).then(
      ([emp, pr, jb, st]) => {
        setEmployee(emp.data.find((e) => e.id === id) || null);
        setRecords(pr.data.filter((p) => p.employee_id === id));
        setJobs(jb.data.filter((j) => j.employee_id === id));
        setCompany(st.data || {});
        setLoaded(true);
      }
    );
  }, [id]);

  const now = new Date();
  const curY = now.getFullYear();
  const curMo = now.getMonth() + 1;

  const inRange = (dateStr) => {
    const p = parseYMD(dateStr);
    if (mode === "all") return true;
    if (!p) return false;
    if (mode === "month") return p.y === curY && p.mo === curMo;
    if (mode === "ytd") return p.y === curY;
    if (mode === "custom") {
      const d = String(dateStr).slice(0, 10);
      if (customStart && d < customStart) return false;
      if (customEnd && d > customEnd) return false;
      return true;
    }
    return true;
  };

  const filteredJobs = useMemo(
    () => jobs.filter((j) => inRange(j.date)).sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [jobs, mode, customStart, customEnd]
  );
  const filteredPayroll = useMemo(
    () => records.filter((r) => inRange(r.period_start || r.created_at)),
    [records, mode, customStart, customEnd]
  );

  const rangeLabel = useMemo(() => {
    if (mode === "month") return `${MONTHS[curMo - 1]} ${curY}`;
    if (mode === "ytd") return `Year-to-Date ${curY}`;
    if (mode === "all") return "All-Time";
    const a = customStart ? formatDate(customStart) : "Start";
    const b = customEnd ? formatDate(customEnd) : "End";
    return `${a} – ${b}`;
  }, [mode, customStart, customEnd, curY, curMo]);

  const doPrint = () => window.print();
  const doPdf = async () => {
    const el = document.getElementById("payroll-print-area");
    if (!el) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = canvas.height * (pageW / canvas.width);
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      const safe = (employee.name || "employee").replace(/\s+/g, "-");
      pdf.save(`Payroll-${safe}-${rangeLabel.replace(/[^a-zA-Z0-9]+/g, "-")}.pdf`);
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  if (loaded && !employee) {
    return (
      <div data-testid="employee-details-page">
        <button onClick={() => navigate("/employees")} className="inline-flex items-center gap-2 text-[#5A189A] font-medium mb-6 hover:text-[#D4AF37] transition">
          <ArrowLeft size={18} /> {t("Back to Employees")}
        </button>
        <div className="bg-white rounded-2xl border border-[#f0ebf7] p-12 text-center text-[#6b647a]">{t("Employee not found")}</div>
      </div>
    );
  }
  if (!employee) return null;

  const s = employeePayrollStats(records);

  const stats = [
    { icon: Clock, label: "Current Month Hours", value: s.monthHours.toFixed(1), accent: "#5A189A" },
    { icon: Briefcase, label: "Current Month Jobs", value: s.monthJobs, accent: "#7B2CBF" },
    { icon: DollarSign, label: "Current Month Payroll", value: money(s.monthPayroll), accent: "#D4AF37" },
    { icon: Clock, label: "Year-to-Date Hours", value: s.ytdHours.toFixed(1), accent: "#0ea5e9" },
    { icon: Briefcase, label: "Year-to-Date Jobs", value: s.ytdJobs, accent: "#16a34a" },
    { icon: DollarSign, label: "Year-to-Date Payroll", value: money(s.ytdPayroll), accent: "#D4AF37" },
    { icon: Wallet, label: "All-Time Payroll", value: money(s.allTimePayroll), accent: "#5A189A" },
    { icon: TrendingUp, label: "Average Pay Per Job", value: money(s.avgPerJob), accent: "#7B2CBF" },
  ];

  const FilterBtn = ({ val, children }) => (
    <button onClick={() => setMode(val)} data-testid={`report-filter-${val}`}
      className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition ${mode === val ? "bg-[#5A189A] text-white" : "bg-[#F1EBFA] text-[#5A189A] hover:bg-[#e7ddf5]"}`}>
      {children}
    </button>
  );

  return (
    <div data-testid="employee-details-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <button onClick={() => navigate("/employees")} data-testid="back-to-employees"
          className="inline-flex items-center gap-2 text-[#5A189A] font-medium hover:text-[#D4AF37] transition">
          <ArrowLeft size={18} /> {t("Back to Employees")}
        </button>
        <PrimaryButton onClick={() => setReportOpen(true)} data-testid="open-payroll-report-button">
          <FileText size={16} /> {t("Print Payroll Report")}
        </PrimaryButton>
      </div>

      {/* Header card */}
      <div className="rounded-2xl p-6 sm:p-8 mb-6 text-white shadow-[0_12px_40px_-16px_rgba(90,24,154,0.6)]"
        style={{ background: "linear-gradient(135deg, #5A189A 0%, #7B2CBF 60%, #9D4EDD 100%)" }}
        data-testid="employee-header-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center font-display text-2xl font-bold text-[#D4AF37] shrink-0"
            style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)" }}>
            {initials(employee.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-bold tracking-tight">{employee.name}</h1>
              {employee.performance && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#D4AF37] text-[#3a2a00]">
                  <Star size={11} /> {employee.performance}
                </span>
              )}
              <StatusBadge status={employee.status} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3 text-sm text-white/90">
              <span>{t(employee.role) || employee.role}</span>
              {employee.crew && <span className="flex items-center gap-1.5"><Users size={14} /> {employee.crew}</span>}
              {employee.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {employee.phone}</span>}
              {employee.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {employee.email}</span>}
              <span className="flex items-center gap-1.5"><DollarSign size={14} /> {money(employee.pay_rate)}/{employee.pay_type === "Hourly" ? "hr" : employee.pay_type === "Per Job" ? "job" : "yr"}</span>
              {employee.availability && <span className="flex items-center gap-1.5"><Calendar size={14} /> {employee.availability}</span>}
            </div>
          </div>
          <div className="rounded-2xl px-5 py-4 text-center shrink-0" style={{ background: "rgba(255,255,255,0.12)" }} data-testid="employee-balance-card">
            <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">{t("Balance")}</div>
            <div className="font-display text-2xl font-bold text-[#D4AF37] mt-1">{money(s.balance)}</div>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="employee-stats-grid">
        {stats.map((st) => (
          <StatCard key={st.label} icon={st.icon} label={t(st.label)} value={st.value} accent={st.accent} testid={`stat-${st.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
        ))}
      </div>

      {/* Payroll history */}
      <h2 className="font-display text-xl font-bold text-[#1a1523] mb-3">{t("Payroll History")}</h2>
      {records.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#f0ebf7] p-10 text-center text-[#6b647a]" data-testid="no-payroll-history">
          {t("No payroll records yet for this employee.")}
        </div>
      ) : (
        <TableShell headers={["Period", "Hours", "Jobs", "Gross", "Net Pay", "Status"]} testid="employee-payroll-table">
          {records.map((r) => (
            <tr key={r.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 text-sm text-[#52525B]">{formatDate(r.period_start) || "-"} → {formatDate(r.period_end) || "-"}</td>
              <td className="py-3.5 px-4 text-sm">{r.hours}</td>
              <td className="py-3.5 px-4 text-sm">{r.jobs_completed}</td>
              <td className="py-3.5 px-4 text-sm">{money(r.gross_pay)}</td>
              <td className="py-3.5 px-4 font-medium text-green-600">{money(r.net_pay)}</td>
              <td className="py-3.5 px-4"><StatusBadge status={r.status} /></td>
            </tr>
          ))}
        </TableShell>
      )}

      {/* Payroll Report modal */}
      <Modal open={reportOpen} onClose={() => setReportOpen(false)} title="Payroll Report" size="xl"
        footer={
          <div className="flex gap-2 no-print">
            <Button variant="outline" onClick={doPdf} disabled={pdfBusy} data-testid="payroll-pdf-button">
              <Download size={15} className="mr-1" /> {pdfBusy ? t("Generating...") : "PDF"}
            </Button>
            <PrimaryButton onClick={doPrint} data-testid="payroll-print-button"><Printer size={15} /> {t("Print")}</PrimaryButton>
          </div>
        }>
        <div className="no-print mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <FilterBtn val="month">{t("This Month")}</FilterBtn>
            <FilterBtn val="ytd">{t("Year-to-Date")}</FilterBtn>
            <FilterBtn val="all">{t("All-Time")}</FilterBtn>
            <FilterBtn val="custom">{t("Custom Range")}</FilterBtn>
          </div>
          {mode === "custom" && (
            <div className="grid grid-cols-2 gap-3 mt-3 max-w-md">
              <Field label="From"><Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="report-custom-start" /></Field>
              <Field label="To"><Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="report-custom-end" /></Field>
            </div>
          )}
        </div>
        <div className="bg-[#f0ebf7] -mx-6 px-2 py-4 rounded-xl overflow-x-auto">
          <div className="mt-card mx-auto" style={{ width: "fit-content" }}>
            <PayrollReport employee={employee} jobs={filteredJobs} payroll={filteredPayroll} rangeLabel={rangeLabel} company={company} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
